import { useContext, useEffect, useCallback } from 'react';
import styled from 'foundations-theming/utils';
import SegmentationFiltersInstanceContext from '../../state/SegmentationFiltersInstanceContext';
import { useEditorActions } from '../../hooks/useEditorActions';
import { CATEGORY_KEYS, getCategoryOptions, getCategoryLabel } from './categorySelection/Categories';
import ObjectSelect from './objectSelection/ObjectSelect';
import CategorySelect from './categorySelection/CategorySelect';
import FilterFamilySelect from './optionSelection/FilterFamilySelect';
import FilterFamilyGroupSelect from './optionSelection/FilterFamilyGroupSelect';
import FilterFamilySubGroupSelect from './optionSelection/FilterFamilySubGroupSelect';
import { useStrategy } from '../../hooks/useStrategy';
import { useFilterFamilySelect } from '../../hooks/useFilterFamilySelect';
import { ObjectTypeIdRegexStrict } from '../../filterQueryFormat/ObjectTypeId';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DataExplorerContainer = styled.div.withConfig({
  displayName: "DataExplorer__DataExplorerContainer",
  componentId: "nkgjbv-0"
})(["display:flex;flex-direction:column;height:100%;width:400px;"]);
const GrowContainer = styled.div.withConfig({
  displayName: "DataExplorer__GrowContainer",
  componentId: "nkgjbv-1"
})(["flex-grow:1;overflow:hidden;"]);
const SELECT_OPTIONS = {
  FILTER_FAMILY: 'FILTER_FAMILY',
  CATEGORY: 'CATEGORY',
  GROUPED_FILTER_FAMILY: 'GROUPED_FILTER_FAMILY'
};
const DataExplorer = () => {
  var _getCategoryLabel;
  const {
    conditionPathToChangeFieldFor,
    draftCondition,
    selectedFilterFamily,
    selectedCategory,
    selectedFilterFamilyGroup,
    selectedObjectType
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    setCategory
  } = useEditorActions();
  const {
    getFilterFamilyOptions,
    getFilterFamilyName
  } = useStrategy();
  const {
    onFilterFamilySelect
  } = useFilterFamilySelect();
  const filterFamilyOptions = getFilterFamilyOptions();
  const categories = getCategoryOptions();
  const enabledCategories = categories.filter(category => filterFamilyOptions.find(option => option.category === category.id));
  const onCategorySelectChange = useCallback(newCategory => {
    // set the category
    setCategory(String(newCategory));

    // if the category is properties, set the filter family too
    if (newCategory === CATEGORY_KEYS.PROPERTIES) {
      const optionGroup = filterFamilyOptions.find(group => group.category === CATEGORY_KEYS.PROPERTIES);
      if (optionGroup) {
        onFilterFamilySelect(optionGroup.options[0]);
      }
    }
  }, [filterFamilyOptions, onFilterFamilySelect, setCategory]);
  const isAddingToExisting = !draftCondition || !draftCondition.filterFamily || !draftCondition.isAddingNew;
  const isAddingToObjectBranch = draftCondition && draftCondition.filterFamily && ObjectTypeIdRegexStrict.test(draftCondition.filterFamily);
  const isChangingField = Boolean(conditionPathToChangeFieldFor);
  const allowCategoryChanges = !isChangingField && (isAddingToExisting || isAddingToObjectBranch);

  // Sets the category tab when none is selected
  useEffect(() => {
    if (!selectedCategory) {
      if (!isChangingField && (isAddingToExisting || isAddingToObjectBranch)) {
        onCategorySelectChange(enabledCategories[0].id);
      }
    }
  }, [selectedCategory, selectedFilterFamily, draftCondition, enabledCategories, isAddingToExisting, isAddingToObjectBranch, isChangingField, onCategorySelectChange]);

  // There should always be exactly one of these options
  let selectOptionsWithin = SELECT_OPTIONS.CATEGORY;
  if (selectedFilterFamily) {
    selectOptionsWithin = SELECT_OPTIONS.FILTER_FAMILY;
  } else if (selectedFilterFamilyGroup) {
    selectOptionsWithin = SELECT_OPTIONS.GROUPED_FILTER_FAMILY;
  }

  // Get the asset label based on the selected option, use for search placeholder and other text
  let assetLabel = (_getCategoryLabel = getCategoryLabel(selectedCategory)) !== null && _getCategoryLabel !== void 0 ? _getCategoryLabel : '';
  if (selectedFilterFamily &&
  // noop but prevents typescript from complaining
  selectOptionsWithin === SELECT_OPTIONS.FILTER_FAMILY) {
    assetLabel = getFilterFamilyName(selectedFilterFamily.filterFamily);
  } else if (selectOptionsWithin === SELECT_OPTIONS.GROUPED_FILTER_FAMILY) {
    var _filterFamilyOptions$, _filterFamilyOptions$2;
    assetLabel = (_filterFamilyOptions$ = (_filterFamilyOptions$2 = filterFamilyOptions.find(group => group.category === selectedCategory)) === null || _filterFamilyOptions$2 === void 0 || (_filterFamilyOptions$2 = _filterFamilyOptions$2.options) === null || _filterFamilyOptions$2 === void 0 || (_filterFamilyOptions$2 = _filterFamilyOptions$2.find(item => item.value === selectedFilterFamilyGroup)) === null || _filterFamilyOptions$2 === void 0 ? void 0 : _filterFamilyOptions$2.text) !== null && _filterFamilyOptions$ !== void 0 ? _filterFamilyOptions$ : '';
  }
  return /*#__PURE__*/_jsxs(DataExplorerContainer, {
    children: [allowCategoryChanges && /*#__PURE__*/_jsx(ObjectSelect, {}), allowCategoryChanges && /*#__PURE__*/_jsx(CategorySelect
    // key is used to force a re-render when the selectedObjectType changes
    , {
      value: selectedCategory,
      onChange: onCategorySelectChange
    }, JSON.stringify(selectedObjectType)), /*#__PURE__*/_jsxs(GrowContainer, {
      children: [selectOptionsWithin === SELECT_OPTIONS.FILTER_FAMILY && (selectedFilterFamily === null || selectedFilterFamily === void 0 ? void 0 : selectedFilterFamily.filterFamily) && /*#__PURE__*/_jsx(FilterFamilySelect, {
        filterFamily: selectedFilterFamily.filterFamily,
        filterFamilyLabel: assetLabel
      }), selectOptionsWithin === SELECT_OPTIONS.CATEGORY && selectedCategory && /*#__PURE__*/_jsx(FilterFamilyGroupSelect, {
        category: selectedCategory,
        categoryLabel: assetLabel
      }), selectOptionsWithin === SELECT_OPTIONS.GROUPED_FILTER_FAMILY && /*#__PURE__*/_jsx(FilterFamilySubGroupSelect, {
        groupLabel: assetLabel
      })]
    })]
  });
};
export default DataExplorer;